import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@core/utils';
import { useRoleBasedMenu } from '@/hooks/use-role-based-menu';
import { useMobileViewport } from '@/hooks/use-mobile-viewport';
import type { MenuItem } from '@/config/menu';

/** Shorter names for a chip; the menu's own label is used for anything not listed. */
const SHORT_LABEL: Record<string, string> = {
  '/bpm/overview': 'Overview',
  '/bpm/add-guest': 'Add guest',
  '/bpm/view-invites': 'Guest invites',
  '/bpm/associate-invites': 'Associate invites',
  '/bpm/guest-checkin': 'Guest check-in',
  '/bpm/associate-checkin': 'Associate check-in',
  '/bpm/schedule': 'Schedule',
  '/bpm/settings': 'Settings',
};

/** The menu group that holds the BPM sub-tools, wherever the plan puts it. */
function findBpmGroup(items: MenuItem[]): MenuItem[] {
  for (const item of items) {
    if (item.children?.some((child) => child.path === '/bpm/overview')) return item.children;
    const nested = item.children ? findBpmGroup(item.children) : [];
    if (nested.length) return nested;
  }
  return [];
}

/**
 * Phones only (`md:hidden`): the BPM sub-tools as one row of chips above every BPM page
 * (decision D27). Inviting, then checking in, then following up is one meeting's work, and
 * without this each switch was Menu → BPM → item. The entries are the user's own menu's BPM
 * group, so BPM Settings appears only for those the menu gives it to. The sticky selection
 * lives in the route's provider, so a switch keeps the chosen meeting.
 *
 * Mounted only on a phone, like the shell's tab bar: building the role-based menu for a row
 * that a wider screen hides would cost every BPM page its access queries for nothing.
 */
export function BpmSubToolBar() {
  return useMobileViewport() ? <SubToolChips /> : null;
}

function SubToolChips() {
  const tools = findBpmGroup(useRoleBasedMenu()).filter((item) => item.path);
  const { pathname } = useLocation();
  const currentRef = useRef<HTMLAnchorElement>(null);

  // Keep the current chip in view when the row is wider than the screen.
  useEffect(() => {
    currentRef.current?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }, [pathname]);

  if (tools.length < 2) return null;

  return (
    <nav
      aria-label="BPM tools"
      className="-mx-3 -mt-1 mb-1 flex gap-2 overflow-x-auto px-3 pb-2 pt-1 [scrollbar-width:none] md:hidden [&::-webkit-scrollbar]:hidden"
    >
      {tools.map((tool) => {
        const path = tool.path as string;
        const current = pathname === path;
        return (
          <Link
            key={path}
            ref={current ? currentRef : undefined}
            to={path}
            aria-current={current ? 'page' : undefined}
            className={cn(
              'flex h-9 shrink-0 items-center rounded-full border px-3.5 text-sm font-medium no-underline',
              current
                ? 'border-[#f4d27f] bg-[#f4d27f]/15 text-[#f4d27f]'
                : 'border-white/15 text-white/80'
            )}
          >
            {SHORT_LABEL[path] ?? tool.label}
          </Link>
        );
      })}
    </nav>
  );
}
